import type { Notification } from '@protector/utils/types';
import type { StaticPointsDateRange } from 'entities/static-points/static-points.models';
import type { ExternalIntegrationSource, StaticPointType } from '@protector/entities/static-point/constants';
import type { StaticPointCreation } from '@protector/entities/static-point/models';
import type {
  LoadSeveritiesRequestType,
  LoadSeveritiesSuccessParamsType,
  Station,
  StationActionType,
  UpsertStationSuccessCallbacks
} from './stations.models';

export const StationsActionsTypes = {
  LOAD_STATIONS: '[Stations] Load Stations',
  LOAD_STATIONS_SUCCESS: '[Stations] Load Stations Success',
  LOAD_STATIONS_FAILURE: '[Stations] Load Stations Failure',
  LOAD_SEVERITIES: '[Stations Severities] Load Stations Severities',
  NO_SEVERITIES_TO_LOAD: '[Stations Severities] No Stations Severities To Load',
  LOAD_SEVERITIES_SUCCESS: '[Stations Severities] Load Stations Success',
  LOAD_SEVERITIES_FAILURE: '[Stations Severities] Load Stations Failure',
  CREATE_STATION_SUCCESS: '[Stations] Create Station Success',
  DELETE_STATION: '[Stations] Delete Station',
  DELETE_STATION_SUCCESS: '[Stations] Delete Stations Success',
  TOGGLE_STATION: '[Stations] Toggle (enable/disable) Station',
  TOGGLE_STATION_LOADING: '[Stations] Loading Toggle (enable/disable) Station',
  TOGGLE_STATION_SUCCESS: '[Stations] Toggle (enable/disable) Station Success',
  EDIT_STATION: '[Stations] Edit Station',
  EDIT_STATION_SUCCESS: '[Stations] Edit Stations Success',
  EDIT_STATION_FAILED: '[Stations] Edit Stations Failed',
  DELETE_TRAPVIEW_DEVICE: '[Stations] Delete Trapview Device'
} as const;

export const LoadStations = (propertyId: string, dateRange?: StaticPointsDateRange) => ({
  type: StationsActionsTypes.LOAD_STATIONS,
  payload: { propertyId, dateRange }
});

export const LoadStationsSuccess = (stations: Station[]) => ({
  type: StationsActionsTypes.LOAD_STATIONS_SUCCESS,
  payload: stations
});

export const LoadStationsFailure = (error: string) => ({
  type: StationsActionsTypes.LOAD_STATIONS_FAILURE,
  payload: error
});

export const LoadAnalyticResultsHistogram = ({
  startDate,
  endDate,
  templateId,
  ids,
  allActions,
  clearCache,
  includePointsWithoutMonitoring
}: LoadSeveritiesRequestType) => ({
  type: StationsActionsTypes.LOAD_SEVERITIES,
  payload: { startDate, endDate, templateId, ids, allActions, clearCache, includePointsWithoutMonitoring }
});

export const LoadAnalyticResultsHistogramSuccess = ({
  severities,
  allActions,
  allIds,
  clearCache,
  allMonitoring,
  range
}: LoadSeveritiesSuccessParamsType) => ({
  type: StationsActionsTypes.LOAD_SEVERITIES_SUCCESS,
  payload: { severities, allActions, allIds, clearCache, allMonitoring, range }
});

export const NoAnalyticResultsHistogramToLoad = (allActions: boolean) => ({
  type: StationsActionsTypes.NO_SEVERITIES_TO_LOAD,
  payload: allActions
});

export const LoadAnalyticResultsHistogramFailure = (error: string) => ({
  type: StationsActionsTypes.LOAD_SEVERITIES_FAILURE,
  payload: error
});

export const CreateStationSuccess = (station: Station, callback: UpsertStationSuccessCallbacks) => ({
  type: StationsActionsTypes.CREATE_STATION_SUCCESS,
  payload: { station, callback }
});

interface Callback {
  notification: Notification;
  tracking?: {
    disabledTrap?: () => void;
    deletedTrap?: () => void;
    editedTrap?: () => void;
  };
  clearDrawerAndSelectStation?: (station?: Station) => void;
}

export const DeleteStation = (
  stationType: StaticPointType | undefined,
  stationId: string | undefined,
  integrationSource: ExternalIntegrationSource | undefined,
  callback: Callback
) => ({
  type: StationsActionsTypes.DELETE_STATION,
  payload: { stationType, stationId, integrationSource, callback }
});

export const DeleteStationSuccess = (
  stationType: StaticPointType,
  stationId: string,
  integrationSource: ExternalIntegrationSource | undefined,
  callback: Callback
) => ({
  type: StationsActionsTypes.DELETE_STATION_SUCCESS,
  payload: { stationType, stationId, integrationSource, callback }
});

export const ToggleStation = (
  stationType: StaticPointType | undefined,
  stationId: string | undefined,
  actionType: StationActionType,
  autoCreateTask: boolean,
  callback: Callback
) => ({
  type: StationsActionsTypes.TOGGLE_STATION,
  payload: { stationType, stationId, actionType, autoCreateTask, callback }
});

export const ToggleStationLoading = (payload: { isLoading: boolean; stationId: string }) => ({
  type: StationsActionsTypes.TOGGLE_STATION_LOADING,
  payload
});

export const ToggleStationSuccess = (station: Station) => ({
  type: StationsActionsTypes.TOGGLE_STATION_SUCCESS,
  payload: station
});

export const EditStation = (stationType: StaticPointType, station: StaticPointCreation, callback: Callback) => ({
  type: StationsActionsTypes.EDIT_STATION,
  payload: { stationType, station, callback }
});

export const EditStationSuccess = (station: Station, callback: Callback) => ({
  type: StationsActionsTypes.EDIT_STATION_SUCCESS,
  payload: { station, callback }
});

export const EditStationFailed = () => ({
  type: StationsActionsTypes.EDIT_STATION_FAILED
});

export const DeleteTrapViewDevice = () => ({
  type: StationsActionsTypes.DELETE_TRAPVIEW_DEVICE
});
